import { PieChart } from '@design/charts/pie-chart/PieChart';
import { roundPercentages } from '@design/charts/pie-chart/utils';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { useTranslation } from 'react-i18next';

import {
  PulseSentimentBreakdown,
  PulseSentimentDirection,
} from '../../pulse-types';
import { SENTIMENT_CONFIG, SENTIMENT_ORDER } from './constants';
import styles from './SentimentBreakdownCard.module.scss';

export const SentimentBreakdownCard = ({
  breakdown,
  onOpenDrawer,
}: {
  breakdown: PulseSentimentBreakdown;
  onOpenDrawer: (direction: PulseSentimentDirection) => void;
}) => {
  const { t } = useTranslation(['widget']);
  const { themeName } = useChartTheme();

  const total = SENTIMENT_ORDER.reduce(
    (sum, sentiment) => sum + breakdown.totals[sentiment],
    0
  );

  const visibleSentiments = SENTIMENT_ORDER.filter(
    sentiment => breakdown.totals[sentiment] > 0
  );
  const percentages = roundPercentages(
    visibleSentiments.map(sentiment => breakdown.totals[sentiment])
  );

  const slices = visibleSentiments.map(sentiment => {
    const { chartColor, labelKey } = SENTIMENT_CONFIG[sentiment];

    return {
      name: t(`widget:themeOverview.pulse.${labelKey}`),
      value: breakdown.totals[sentiment] / total,
      color: chartColor,
    };
  });

  return (
    <div
      className={styles.root}
      data-testid="pulse-sentiment-breakdown"
      onClick={() => onOpenDrawer('all')}
    >
      <div className={styles.donutRow}>
        {total > 0 ? (
          <div className={styles.pieChart} data-testid="pulse-sentiment-donut">
            <PieChart
              data={slices}
              theme={themeName}
              formatLegendValue={(_value, index) => `${percentages[index]}%`}
              disableAnimation
            />
          </div>
        ) : (
          <div className={styles.legend}>
            {SENTIMENT_ORDER.map(sentiment => {
              const { className, labelKey } = SENTIMENT_CONFIG[sentiment];

              return (
                <div key={sentiment} className={styles.legendRow}>
                  <span className={styles.legendName}>
                    <span className={`${styles.legendDot} ${className}`} />
                    {t(`widget:themeOverview.pulse.${labelKey}`)}
                  </span>
                  <span className={styles.legendValue}>—</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
